import React from 'react'
import { BeatLoader as Loader } from 'react-spinners'
import { ItemModel } from '@leverege/ui-attributes'
import { ExportActionMutator, KeysLinksMutator, TrackClusterToggleMutator } from '@leverege/recovr-plugins'
import { GlobalState } from '@leverege/ui-redux'

import Theme from './theme'
import ActionPersonaMutator from './mutators/ActionPersonaMutator'
import LimitToRolesMutator from './mutators/LimitToRolesMutator'
import BillingLinkMutator from './mutators/BillingLinkMutator'
import { getInitAttributes, getLoginAttributes } from './intercom'

const adminHandles = ( context ) => {
  // hide for all but steve on mobile
  // const { clientProps : { mobile } } = context 
  const hasDefault = !!GlobalState.get()?.ui?.personas?.some( p => p.name === 'default' )
  // only non-custom persona user's when on mobile
  return hasDefault
}

function create( env = process.env ) {

  const isDev = process.env.IS_DEV === 'true'

  return {
    uiName : process.env.userManagementUiName || 'ui',
    r4kEnabled : process.env.R4K_ENABLED,
    molten : {
      splashClass : Loading,
    },
    theme : {
      theme : Theme,

      // --------------------------------------------------------------------------------------------------------
      // The lot management app (recovr-ui) uses the standard recovr ui builder theme
      // The ID of a ui builder project is not the same as its url, so both are listed here for convenience
      // UI builder project id: 7b2GTbmsZj3f5VdFpjGM8O
      // Link to UI Builder project: https://builder.leverege.com/appearance/0wPpbX4yh6a6ZVwRYDBbg7/elements/Text
      // --------------------------------------------------------------------------------------------------------
      appearanceProjectId : process.env.APPEARANCE_PROJECT_ID || '7b2GTbmsZj3f5VdFpjGM8O',
      useActiveTheme : isDev
    },
    maps : {
      apiKey : process.env.GOOGLE_MAPS_API_KEY,
      mapId : process.env.GOOGLE_MAPS_MAP_ID,
    },
    api : {
      host : process.env.IMAGINE_HOST || 'http://localhost:8181',
      systemId : process.env.SYSTEM_ID,
      projectId : process.env.PROJECT_ID,
    },
    googleAnalytics : { 
      trackerId : process.env.GOOGLE_ANALYTICS_TRACKER_ID,
    },
    amplitude : {
      apiKey : env.AMPLITUDE_API_KEY || process.env.AMPLITUDE_API_KEY,
      sampleRate : 0.01
    },
    sentry : {
      dsn : 'https://745cbd04ae83c093d1fdea7c4cc3d580@o4505749264072704.ingest.sentry.io/4505760837402624',
      enabled : !process.env.WEBPACK_SERVE,
      environment : process.env.DEPLOYMENT_TARGET,
      replaysSessionSampleRate : isDev ? 0 : 0.01,
      replaysOnErrorSampleRate : isDev ? 0.1 : 0.5
    },
    DetailTypes : [ {
      model : ItemModel,
      settingsPath : 'listModel',
      actionName : 'List',
      routePath : '/settings/list',
      icon : 'fa fa-list fa-fw'
    }, {
      model : ItemModel,
      settingsPath : 'associationModel',
      actionName : 'Association',
      routePath : '/settings/association',
      icon : 'fa fa-plug fa-fw'
    } ],
    plugins : {
      // LoadConfig will use these and call Config.set()
      config : {
        LocalCanResolver : {
          compareModeActive : false,
        },
        'molten.dataViewers.layout.RenameLayoutAction' : {
          handles : adminHandles
        },
        'molten.dataViewers.layout.DeleteLayoutAction' : {
          handles : adminHandles
        },
        'molten.dataViewers.layout.SetLayoutAsDefaultAction' : {
          handles : adminHandles
        },
        cypress : {
          button : {
            'sales.voidSale' : 'button.sales.voidSale',
            'sales.existingUser' : 'button.sales.existingUser'
          },
          input : {
            'device.search' : 'input.device.search',
            'sales.existingUser' : 'input.sales.existingUser',
            'sales.accountEmail' : 'input.sales.accountEmail',
            'sales.fullName' : 'input.sales.fullName',
            'sales.phoneNumber' : 'input.sales.phoneNumber',
            'sales.address' : 'input.sales.address',
            'sales.addressLineTwo' : 'input.sales.addressLineTwo',
            'sales.city' : 'input.sales.city',
            'sales.state' : 'input.sales.state',
            'sales.zipCode' : 'input.sales.zipCode'
          },
          text : {
            'locator.unpairFromVehicle' : 'text.locator.unpairFromVehicle',
            'locator.offline' : 'text.locator.offline',
            'sales.locatorUnactivated' : 'text.sales.locatorUnactivated',
            'sales.voidSaleMsg' : 'text.sales.voidSaleMsg'
          }
        },
        VehicleSettings : {
          usageTypeEnum : env.VEHICLE_USAGE_TYPE_ENUM || process.env.VEHICLE_USAGE_TYPE_ENUM
        },
        RecovrServer : {
          billingModule : process.env.RECOVR_BILLING_MODULE || 'recovr'
        },
        GroupScreen : {
          defaultPerPage : 200
        },
        OperationStates : {
          operationStates : {
            1 : 'Shipment',
            2 : 'Factory Default',
            8 : 'Unpaired',
            9 : 'Paired',
            10 : 'Sold',
            11 : 'Active Theft Recovery',
            12 : 'Disabled'
          }          
        },
        Intercom : {
          appId : env.INTERCOM_APP_ID || process.env.INTERCOM_APP_ID,
          getInitAttributes,
          getLoginAttributes,
          chatLinkAppearance : {
            id : 'customer-support',
            name : 'Message Support'
          },
          // eslint-disable-next-line max-len
          helpCenterLinkUrl : 'https://help.recovr.biz/en/collections/2846113-recovr-dealer-academy'
        },
        About : {
          appearance : {
            name : 'About RecovR'
          },
          url : 'https://www.recovr.biz/who-is-behind-recovr'
        },
        TermsAndConditions : {
          url : 'https://intercom.help/Kudelski/en/collections/2846113-recovr-training-videos-for-car-dealerships'
        },
        ItemScreen : {
          collapseOpenIcon : 'https://recovr-ui-assets.storage.googleapis.com/chevron-right-blue.png',
          collapseCloseIcon : 'https://recovr-ui-assets.storage.googleapis.com/chevron-left-blue.png',
        },
        InactivityScreen : {
          title : React.createElement( 'img', {
            src : 'https://recovr-ui-assets.storage.googleapis.com/logo-dark.png',
            width : 150
          } ),
          image : [
            'https://recovr-ui-assets.storage.googleapis.com/broken-hourglass.png',
            'https://recovr-ui-assets.storage.googleapis.com/sleepy-car.png'
          ]
        },
        VoidSale : {
          saleLocationParentPath : process.env.SALE_LOCATION_PARENT_PATH
        },
        LayoutViewerSelector : {
          allowEditing : 'default'
        },
        mapBoxPosition : {
          selectedRezoomThreshold : 16
        },
        LoginScreen : {
          oidcClientFilter : [ { regex : '^((?!carmax).)*$' } ], // hide carmax OIDC
          userText : 'Account Email'
        },
        'VehicleNetwork.dealer' : {
          id : process.env.DEALER_VEHICLE_NETWORK_ID || process.env.VEHICLE_NETWORK_ID || 'prd-auto-vehicles',
          aliasKey : 'deviceId'
        },
        'VehicleNetwork.consumer' : {
          id : process.env.CONSUMER_VEHICLE_NETWORK_ID || 'prd-customer-vehicles',
          aliasKey : 'deviceId'
        },
        ConsumerLocation : {
          id : process.env.CONSUMER_LOCATION_ID
        },
        LocatorNetwork : {
          id : process.env.LOCATOR_NETWORK_ID || 'prd-recovr-tracker',
          aliasKey : 'serialNumber'
        },
        ResponsiveDataViewer : {
          minWidth : 640
        },
        Main : {
          userIcon : 'https://recovr-ui-assets.storage.googleapis.com/user.png'
        },
        AccountScreen : {
          redirectUrl : '/account/profile'
        },
        ProfileScreen : {
          usernameSubtitle : 'This is the email associated with your account. This cannot be changed.',
          usernameLabel : 'Account Email'
        },
        LoadingScreen : {
          spinner : Loader,
          props : { size : 8 }
        },
        LostScreen : {
          image : [
            'https://molten-ui-assets.storage.googleapis.com/404-UFO.png',
            'https://molten-ui-assets.storage.googleapis.com/404-Earth.png',
            'https://molten-ui-assets.storage.googleapis.com/404-Page.png',
            'https://molten-ui-assets.storage.googleapis.com/404-Ghost.png'
          ]
        },
        ResourceUsers : {
          screenIcon : 'https://recovr-ui-assets.storage.googleapis.com/users-title.png',
          navIcon : 'https://recovr-ui-assets.storage.googleapis.com/users-nav.png',
          userSelectorUsernameLabel : 'Account Email',
          userSelectorUsernameHint : 'Enter Account Email',
          userSelectorUsernameInstructions : "Enter user's account email and hit enter or next to continue.",
          createUsernameLabel : 'Account Email',
          createUsernameHint : 'Enter Account Email',
          allowCreateUser : false,
          userSearchField : 'username',
          oidcClientFilter : [ { regex : '^((?!carmax).)*$' } ]
        },
        'blueprint.building.ItemScreen' : {
          // redirectUrl : {
          //   from : '/p/:persona/building/:buildingId',
          //   to : '/p/:persona/building/:buildingId/zones'
          // }
        },
        Filtering : {
          attributeNameMap : {
            'vehicle.color' : null,
            'vehicle.swatchAndColor' : 'filter.recovr.colorCustom',
            'vehicle.calculatedAlerts' : 'filter.calculatedAlerts',
            'locator.operationStateChangeStatus' : null,
            'vehicle.locatorPaired' : 'filter.locatorPaired',
            'locator.businessState' : 'filter.aliasEquals.businessState',
            'sale.reconciliation.status' : 'filter.aliasEquals.reconciliationStatus',
            'locator.events.resetRequestState' : 'filter.aliasEquals.resetRequestState'
          }
        },
        ZoneVehicleAdapter : {
          accuracyMultiplier : 5
        },
        AlertRules : {
          screenIcon : 'https://recovr-ui-assets.storage.googleapis.com/icon-screen-alerts.png',
          navIcon : 'https://recovr-ui-assets.storage.googleapis.com/icon-alert-rules.png'
        }
      },
      excludes : [
        'blueprint.action.location.sales.UpdateItem',
        'blueprint.action.company.locations.sales.UpdateItem',
        'blueprint.action.company.sales.UpdateItem',

        // hide zone's beacons list
        'blueprint.location.zones.beacons.GroupScreen',
        'blueprint.link.location.zones.beacons.GroupScreen',
        'blueprint.company.locations.zones.beacons.GroupScreen',
        'blueprint.link.company.locations.zones.beacons.GroupScreen',

        // hide Create Location
        'blueprint.action.company.locations.CreateItem',
        'blueprint.action.location.CreateItem',

        // hide locator drilldown under vehicle
        'blueprint.location.vehicles.locator.ItemScreen',
        'blueprint.company.locations.vehicles.locator.ItemScreen',
        'blueprint.company.vehicles.locator.ItemScreen',
        // not really a group screen, so a bit odd
        'blueprint.link.location.vehicles.locator.GroupScreen',
        'blueprint.link.company.locations.vehicles.locator.GroupScreen',
        'blueprint.link.company.vehicles.locator.GroupScreen',

        // hide case's vehicle page 
        'blueprint.link.location.cases.vehicle.GroupScreen',
        'blueprint.location.cases.vehicle.ItemScreen',
        'blueprint.link.company.locations.cases.vehicle.GroupScreen',
        'blueprint.company.locations.cases.vehicle.ItemScreen',
        'blueprint.link.company.cases.vehicle.GroupScreen',
        'blueprint.company.cases.vehicle.ItemScreen',

        // Hide the Terms & Conditions link
        'molten.link.TermsAndConditions',

        // hide device mgmt specific plugs from plugins lib
        'recovr.link.company.locations.jobs.GroupScreen',
        'recovr.company.locations.jobs.GroupScreen',
        'recovr.location.jobs.GroupScreen',
        'recovr.link.location.jobs.GroupScreen',
        'recovr.link.job.GroupScreen',
        'recovr.job.GroupScreen',
        
        // Hide pairing at the company level
        'recovr.company.vehicles.locator.pair',
        'recovr.vehicle.locator.pair',
        // Hide create new payment action
        'blueprint.action.payment.CreateItem',
        'blueprint.action.bill.payments.CreateItem',

        'upload.locator.manufacturing.log',
        'revert.locator.manufacturing.log',
        'upload.locator.shipment.log',
        'upload.beacon.shipment.log',
        'revert.beacon.shipment.log',
        'upload.gateway.manufacturing.log',
        'revert.gateway.manufacturing.log',
        'upload.gateway.shipment.log',
        'upload.tag.manufacturing.log',
        'revert.tag.manufacturing.log',
        'upload.tag.shipment.log',
        'revert.tag.shipment.log',
        'r4k.locator.ConvertToGateway',

        // hide locator reset
        'recovr.locator.Reset',
        'recovr.locator.CancelReset',
      ],
      mutators : [
        // array of functions( type, plugin ) that can return the plugin to continue,
        // null to exclude it, or a plugin to replace it.
        ActionPersonaMutator,
        LimitToRolesMutator,
        // CaseHistoryMutator,
        BillingLinkMutator,
        ExportActionMutator,
        KeysLinksMutator,
        TrackClusterToggleMutator
      ],
      modelTypes : {
        caseHistoryModel : 'recovr.mapRenderer.case.HistoryModel'
      }
    },
    actions : {
      'molten.dataViewers.list.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.table.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.screens.group.search.SearchSettings' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.details.EditDetailsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.map.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.history.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.aggregations.ContainerEditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.aggregations.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.layout.DefaultLayoutAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.layout.SaveAsLayoutAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.layout.ResetLayoutAction' : {
        hideForNonAdmin : true
      },
      'molten.dataViewers.layout.SaveLayoutAction' : {
        hideForNonAdmin : true
      },
      'list.SettingsAction' : {
        hideForNonAdmin : true
      },
      'table.SettingsAction' : {
        hideForNonAdmin : true
      },
      'search.EditSearchSettingsActionMobile' : {
        hideForNonAdmin : true
      },
      'search.EditSearchSettingsAction' : {
        hideForNonAdmin : true
      },
      'molten.mapDataViewer.EditSettingsAction' : {
        hideForNonAdmin : true
      },
      'link.desktop.Settings' : {
        hideForNonAdmin : true
      },
      'link.mobile.Settings' : {
        hideForNonAdmin : true
      },
      'dataCard.SettingsAction' : {
        hideForNonAdmin : true
      },
      'dashboard.ToggleEditingAction' : {
        hideForNonAdmin : true
      },
      'aggregations.SettingsAction' : {
        hideForNonAdmin : true
      },
      'layout.AdvancedSettingsAction' : {
        hideForNonAdmin : true
      },
      'recovr.action.TheftRecovery' : {
        limitToRoles : [
          'default',
          'dealeradmin',
          'companyadmin',
          'dealerCompanyAdmin',
          'dealerLocationAdmin',
          'inventorymanager'
        ]
      },
      'molten.layout.EditLayoutAction' : {
        hideForNonAdmin : true
      },
      'history.SettingsAction' : {
        hideForNonAdmin : true
      },
      'recovr.link.location.LocationAlerts' : {
        limitToRoles : [
          'default',
          'dealeradmin',
          'companyadmin',
          'kudelskisupport',
          'Engineer',
          'salesperson',
          // NEW ROLES
          'dealerLocationAdmin', // dealeradmin
          'dealerCompanyAdmin',
          'internalAdmin', // kudelskisupport
          'internalEngineer', // Engineer
          'internalEditor',
          'internalSupportViewer',
          'internalAccounting',
          'deploymentRoleAssigner',
        ]
      },
      'recovr.link.company.locations.LocationAlerts' : {
        limitToRoles : [
          'default',
          'dealeradmin',
          'companyadmin',
          'kudelskisupport',
          'Engineer',
          'salesperson',
          // NEW ROLES
          'dealerLocationAdmin', // dealeradmin
          'dealerCompanyAdmin',
          'internalAdmin', // kudelskisupport
          'internalEngineer', // Engineer
          'internalEditor',
          'internalSupportViewer',
          'internalAccounting',
          'deploymentRoleAssigner',
        ]
      },
      'recovr.location.LocationAlertScreen' : {
        limitToRoles : [
          'default',
          'dealeradmin',
          'companyadmin',
          'kudelskisupport',
          'Engineer',
          'salesperson',
          // NEW ROLES
          'dealerLocationAdmin', // dealeradmin
          'dealerCompanyAdmin',
          'internalAdmin', // kudelskisupport
          'internalEngineer', // Engineer
          'internalEditor',
          'internalSupportViewer',
          'internalAccounting',
          'deploymentRoleAssigner',
        ]
      },
      'recovr.company.locations.LocationAlertScreen' : {
        limitToRoles : [
          'default',
          'dealeradmin',
          'companyadmin',
          'kudelskisupport',
          'Engineer',
          'salesperson',
          // NEW ROLES
          'dealerLocationAdmin', // dealeradmin
          'dealerCompanyAdmin',
          'internalAdmin', // kudelskisupport
          'internalEngineer', // Engineer
          'internalEditor',
          'internalSupportViewer',
          'internalAccounting',
          'deploymentRoleAssigner',
        ]
      },
      'recovr.voidSale' : {
        limitToRoles : [
          'default',
          'dealeradmin',
          'companyadmin',
          'kudelskisupport',
          'Engineer',
          'salesperson',
          'inventorymanager',
          'fimanager',
          // NEW ROLES
          'dealerLocationAdmin', // dealeradmin
          'dealerCompanyAdmin',
          'internalAdmin', // kudelskisupport
          'internalEngineer', // Engineer
          'dealerEditor', // inventorymanager, fimanager, porter
        ]
      },
      'recovr.salesFlow' : {
        limitToRoles : [
          'default',
          'dealeradmin',
          'companyadmin',
          'kudelskisupport',
          'Engineer',
          'salesperson',
          'inventorymanager',
          'fimanager',
          // NEW ROLES
          'dealerLocationAdmin', // dealeradmin
          'dealerCompanyAdmin',
          'internalAdmin', // kudelskisupport
          'internalEngineer', // Engineer
          'dealerEditor', // inventorymanager, fimanager, porter
        ]
      },
    }
  }
}

function Loading( props ) {
  return React.createElement( 'div', null, 'Loading Molten...' )
}
export default {
  create
}
